import { useState } from 'react';
import { useCreateThread } from '@/api';
import { useStudioCreateThread } from '@/api/studio';
import type { Task, Thread } from '@/api/types';
import { errorMessage } from '@/lib/errors';
import { Dialog } from './ui';

/**
 * «Новое обсуждение»: a title, optionally the task it is about, and the first message.
 * `tasks` are the ones this side may pick (the client's visible ones; the studio's all).
 */
export function NewDiscussionDialog({
  open,
  onClose,
  projectId,
  tasks,
  taskId,
  studio = false,
  onCreated,
}: {
  open: boolean;
  onClose: () => void;
  projectId: string;
  tasks: Task[];
  /** Preselected task. */
  taskId?: string;
  studio?: boolean;
  onCreated: (t: Thread) => void;
}) {
  const clientCreate = useCreateThread();
  const studioCreate = useStudioCreateThread();
  const create = studio ? studioCreate : clientCreate;
  const [title, setTitle] = useState('');
  const [task, setTask] = useState(taskId ?? '');
  const [body, setBody] = useState('');
  const ready = title.trim() && body.trim() && !create.isPending;

  return (
    <Dialog open={open} onClose={onClose} title="Новое обсуждение">
      <form
        className="space-y-4 p-5"
        onSubmit={(e) => {
          e.preventDefault();
          if (!ready) return;
          create.mutate(
            { projectId, title: title.trim(), taskId: task || undefined, body: body.trim() },
            {
              onSuccess: (t) => {
                setTitle('');
                setBody('');
                onClose();
                onCreated(t);
              },
            },
          );
        }}
      >
        <label className="block text-sm">
          <span className="mb-1.5 block text-muted">О чём поговорить</span>
          <input className="field" value={title} onChange={(e) => setTitle(e.target.value)} maxLength={200} placeholder="Например, правки по макету главной" autoFocus />
        </label>
        <label className="block text-sm">
          <span className="mb-1.5 block text-muted">По задаче</span>
          <select className="field" value={task} onChange={(e) => setTask(e.target.value)}>
            <option value="">Без задачи, общий вопрос по проекту</option>
            {tasks.map((t) => (
              <option key={t.id} value={t.id}>
                {t.key}, {t.title}
              </option>
            ))}
          </select>
        </label>
        <label className="block text-sm">
          <span className="mb-1.5 block text-muted">Первое сообщение</span>
          <textarea className="field min-h-24" value={body} onChange={(e) => setBody(e.target.value)} maxLength={10000} placeholder={studio ? 'Что хотите обсудить с клиентом' : 'Что хотите обсудить с командой'} />
        </label>
        {create.isError && (
          <p role="alert" className="text-sm text-overdue">
            {errorMessage(create.error)}
          </p>
        )}
        <div className="flex justify-end gap-2 border-t border-line pt-4">
          <button type="button" className="btn-secondary" onClick={onClose}>
            Отмена
          </button>
          <button className="btn-primary" disabled={!ready}>
            Начать обсуждение
          </button>
        </div>
      </form>
    </Dialog>
  );
}
